/**
 * Multiplayer Session Manager - Roll20-style Session Invitation System
 * date_of_change: 2025-09-24
 *
 * Provides functionality similar to Roll20's session management:
 * - Create/join combat sessions
 * - Generate invitation links
 * - Manage session participants
 * - Handle session visibility and permissions
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { leaveCombatSessionMembership } from '../../services/combatSessionMembership';
import { logMultiplayer } from '../../utils/DebugLogger';
import {
  builtinRulesetSelection,
  copyRulesetSelection,
  storedRulesetSelection
} from '../../rules/rulesetSelections';
import './MultiplayerSessionManager.css';

const MultiplayerSessionManager = ({ onSessionJoined, onSessionCreated }) => {
  const { user } = useUser();
  const {
    isConnected,
    currentSessionId,
    sessionParticipants,
    joinSession,
    leaveSession
  } = useWebSocket();

  // Session management state
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [showJoinSession, setShowJoinSession] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionDescription, setNewSessionDescription] = useState('');
  const [sessionPassword, setSessionPassword] = useState('');
  const [joinSessionId, setJoinSessionId] = useState('');
  const [joinPassword, setJoinPassword] = useState('');
  // date_of_change: 2026-04-19 - Battle alias for participant display name
  const [battleAlias, setBattleAlias] = useState(() => {
    try { return localStorage.getItem('vcs_battle_alias') || ''; } catch (_) { return ''; }
  });
  const [aliasInput, setAliasInput] = useState('');
  const [editingAlias, setEditingAlias] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [availableSessions, setAvailableSessions] = useState([]);
  const [rulesetOptions, setRulesetOptions] = useState([]);
  const [selectedRulesetKey, setSelectedRulesetKey] = useState('');
  const [rulesetLoadMessage, setRulesetLoadMessage] = useState('');
  const [sessionActionMessage, setSessionActionMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const leaveInFlightRef = useRef(new Set());
  loadingRef.current = loading;

  // @date_of_change: 2025-09-27 - Client-side idempotency to prevent duplicate sessions
  const [createSessionIdempotencyKey, setCreateSessionIdempotencyKey] = useState(null);

  // Fetch sessions visible to the current Clerk user (member/invited/owner)
  // date_of_change: 2025-09-25 - Clerk-aware sessions listing
  const fetchAvailableSessions = async ({ preserveExisting = false } = {}) => {
    try {
      const { apiFetch } = await import('../../utils/apiFetch.js');
      const res = await apiFetch('/api/combat', { method: 'GET' });
      if (!res.ok) throw new Error(`List failed: ${res.status}`);
      const sessions = await res.json();
      const normalizedSessions = Array.isArray(sessions) ? sessions : [];
      setAvailableSessions(normalizedSessions);
      return normalizedSessions;
    } catch (e) {
      console.error('[MultiplayerSessionManager] Failed to fetch sessions:', e);
      if (!preserveExisting) {
        setAvailableSessions([]);
      }
      return null;
    }
  };

  // Extract a sessionId from various inputs (plain ID or invite URL)
  // date_of_change: 2025-09-25 - Robust join parsing for full URLs
  const extractSessionId = (input) => {
    if (!input) return '';
    // Prefer a 24-hex match anywhere in the string
    const hex = input.match(/[a-f0-9]{24}/i);
    if (hex) return hex[0];
    // As a fallback, look for /battle/:id segments
    try {
      // If it's a valid URL, parse pathname
      const url = new URL(input);
      const m = url.pathname.match(/\/battle\/([a-f0-9]{24})/i);
      if (m) return m[1];
    } catch (_e) {
      // Not a full URL; ignore
    }
    return input.trim();
  };

  // Generate session invitation link
  const generateInviteLink = (sessionId, password = null) => {
    const baseUrl = window.location.origin;
    let link = `${baseUrl}/battle/${sessionId}`;

    if (password) {
      // Encode password in URL params for easy joining
      link += `?password=${encodeURIComponent(password)}`;
    }

    return link;
  };

  // Create a new multiplayer session
  const handleCreateSession = async () => {
    if (!newSessionName.trim()) {
      alert('Please enter a session name');
      return;
    }
    const selectedRuleset = rulesetOptions.find((option) => option.key === selectedRulesetKey);
    if (!selectedRuleset) {
      alert('Choose a ruleset for this Battle Room.');
      return;
    }

    // @date_of_change: 2025-09-27 - Idempotency key to prevent double-clicks creating duplicates
    const idempotencyKey = `create_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    // Check if we're already creating a session
    if (createSessionIdempotencyKey === idempotencyKey || loading) {
      logMultiplayer('Session creation already in progress, ignoring duplicate request');
      return;
    }

    setCreateSessionIdempotencyKey(idempotencyKey);
    setLoading(true);
    try {
      // @date_of_change: 2025-09-25 - Fixed session creation flow
      // Step 1: Create session on backend and await authoritative sessionId
      const { apiFetch } = await import('../../utils/apiFetch.js');
      const backendUrlResolver = (await import('../../utils/BackendUrlResolver.js')).default;
      const backendBase = await backendUrlResolver.getBackendUrl();

      logMultiplayer('Creating session on backend:', backendBase);

      // @date_of_change: 2025-09-25 - Match backend API expectations {name, map}
      const sessionPayload = {
        name: newSessionName.trim(),
        map: null,
        rulesetSelection: copyRulesetSelection(selectedRuleset.selection)
      };

      // @date_of_change: 2025-09-25 - Use apiFetch with automatic Clerk authentication
      const response = await apiFetch('/api/combat', {
        method: 'POST',
        body: JSON.stringify(sessionPayload)
      });

      if (!response.ok) {
        throw new Error(`Create failed: ${response.status}`);
      }

      const sessionData = await response.json();
      const sessionId = sessionData._id; // Backend returns session object with _id field
      logMultiplayer('Backend created session:', sessionId);

      // Generate invitation link with backend-provided session ID
      const link = generateInviteLink(sessionId, null); // No password support in current backend
      setInviteLink(link);

      // Step 2: Now join over WebSocket using the authoritative sessionId
      logMultiplayer('Joining session via WebSocket:', sessionId);
      // Ensure membership recorded on backend
      await apiFetch(`/api/combat/${sessionId}/join`, { method: 'POST' });
      await joinSession(sessionId);

      logMultiplayer('Successfully created and joined session:', sessionId);

      // Notify parent component with backend session data
      if (onSessionCreated) {
        onSessionCreated({
          id: sessionId,
          name: sessionData.name,
          description: newSessionDescription.trim(), // Keep UI-entered description for display
          gamemaster: sessionData.dmId,
          gamemasterName: user.fullName || user.firstName || 'Game Master'
        });
      }

      // Reset form
      setNewSessionName('');
      setNewSessionDescription('');
      setSessionPassword('');
      setShowCreateSession(false);

      // Refresh available sessions list
      fetchAvailableSessions();

    } catch (error) {
      console.error('[MultiplayerSessionManager] Failed to create session:', error);
      alert('Failed to create session. Please try again.');
    } finally {
      setLoading(false);
      // @date_of_change: 2025-09-27 - Reset idempotency key to allow future session creation
      setCreateSessionIdempotencyKey(null);
    }
  };

  // Join an existing session
  const handleJoinSession = async () => {
    if (!joinSessionId.trim()) {
      alert('Please enter a Teleportation Circle');
      return;
    }

    setLoading(true);
    try {
      const { apiFetch } = await import('../../utils/apiFetch.js');
      const sid = extractSessionId(joinSessionId.trim());
      // date_of_change: 2025-09-25 - Record membership before WS join
      await apiFetch(`/api/combat/${sid}/join`, { method: 'POST' });
      await joinSession(sid);

      logMultiplayer('Joined session:', joinSessionId);

      // Notify parent component with sanitized id
      // date_of_change: 2025-09-25 - Use parsed sid to avoid leaking full URLs upstream
      if (onSessionJoined) {
        onSessionJoined(sid);
      }

      // Reset form
      setJoinSessionId('');
      setJoinPassword('');
      setShowJoinSession(false);

    } catch (error) {
      console.error('[MultiplayerSessionManager] Failed to join session:', error);
      alert('Failed to join session. Please check the Teleportation Circle and try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Keep Leave truthful and idempotent: one authoritative request may be in
   * flight per room, and success is shown only after the refreshed room list
   * no longer contains that room. date_of_change: 2026-08-24
   */
  const performSessionLeave = async (session) => {
    const sessionId = String(session?._id || '');
    if (!sessionId || leaveInFlightRef.current.has(sessionId)) return;

    const sessionName = session?.name || 'this Battle Room';
    leaveInFlightRef.current.add(sessionId);
    setSessionActionMessage({
      tone: 'pending',
      text: `Leaving ${sessionName}… Its room card will remain until the server confirms the change.`,
    });

    try {
      if (currentSessionId === sessionId) {
        await leaveSession();
        setInviteLink('');
      } else {
        await leaveCombatSessionMembership(sessionId);
      }

      const refreshedSessions = await fetchAvailableSessions({ preserveExisting: true });
      if (!refreshedSessions) {
        setSessionActionMessage({
          tone: 'warning',
          text: `${sessionName} was left, but the room list could not refresh. Reload the dashboard to confirm the result before trying again.`,
        });
        return;
      }
      if (refreshedSessions.some((availableSession) => availableSession?._id === sessionId)) {
        throw new Error(`${sessionName} is still listed after the server response. The room remains available; wait for the connection to recover before trying again.`);
      }

      setSessionActionMessage({ tone: 'success', text: `Left ${sessionName}.` });
    } catch (error) {
      console.warn('[MultiplayerSessionManager] Leave failed:', error);
      setSessionActionMessage({
        tone: 'error',
        text: error?.message || 'Could not leave this Battle Room. The room remains unchanged. Check your connection and try again.',
      });
    } finally {
      leaveInFlightRef.current.delete(sessionId);
    }
  };

  // Leave the room currently attached to the realtime context.
  const handleLeaveSession = async () => {
    if (!currentSessionId) return;
    const currentSession = availableSessions.find((session) => session?._id === currentSessionId) || {
      _id: currentSessionId,
      name: 'the active Battle Room',
    };
    await performSessionLeave(currentSession);
  };

  // Load immutable built-ins and accessible compatible stored rulesets only
  // while the creation dialog is open. The selected option is sent as an
  // explicit contract, never inferred by the backend. date_of_change: 2026-08-21
  useEffect(() => {
    if (!showCreateSession) return undefined;
    let cancelled = false;
    const loadRulesets = async () => {
      setRulesetLoadMessage('Checking available rulesets…');
      try {
        const { apiFetch } = await import('../../utils/apiFetch.js');
        const [presetResponse, storedResponse] = await Promise.all([
          apiFetch('/api/ruleset/presets', { method: 'GET', requireAuth: true }),
          apiFetch('/api/ruleset', { method: 'GET', requireAuth: true })
        ]);
        if (!presetResponse.ok || !storedResponse.ok) throw new Error('Rulesets could not be listed.');
        const presets = await presetResponse.json();
        const stored = await storedResponse.json();
        if (cancelled) return;
        const builtinOptions = (Array.isArray(presets) ? presets : [])
          .filter((ruleset) => ruleset?.presetId && ruleset?.name)
          .map((ruleset) => ({
            key: `builtin:${ruleset.presetId}`,
            label: `${ruleset.name} · Built in`,
            selection: builtinRulesetSelection(ruleset)
          }));
        const storedOptions = (Array.isArray(stored) ? stored : [])
          .filter((ruleset) => ruleset?.packageId && ruleset?.rulesetVersionId && ruleset?.definitionHash && ruleset?.name)
          .map((ruleset) => {
            return {
              key: `stored:${ruleset.packageId}:${ruleset.rulesetVersionId}`,
              label: `${ruleset.name} · ${ruleset.edition || 'custom'}`,
              selection: storedRulesetSelection(ruleset)
            };
          });
        const nextOptions = [...builtinOptions, ...storedOptions];
        if (nextOptions.length > 0) {
          setRulesetOptions(nextOptions);
          setSelectedRulesetKey((current) => (
            nextOptions.some((option) => option.key === current) ? current : nextOptions[0].key
          ));
        }
        setRulesetLoadMessage(storedOptions.length > 0
          ? `${storedOptions.length} saved ruleset${storedOptions.length === 1 ? '' : 's'} available.`
          : 'Built-in core rules selected.');
      } catch (error) {
        if (!cancelled) {
          setRulesetLoadMessage(error?.message || 'Rulesets could not be loaded. Room creation is paused until an exact ruleset is available.');
        }
      }
    };
    loadRulesets();
    return () => { cancelled = true; };
  }, [showCreateSession]);

  useEffect(() => {
    if (!showCreateSession && !showJoinSession) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => {
      document.getElementById(showCreateSession ? 'session-name' : 'join-session-id')?.focus();
    }, 0);
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !loadingRef.current) {
        if (showCreateSession) setShowCreateSession(false);
        if (showJoinSession) setShowJoinSession(false);
        return;
      }
      if (event.key !== 'Tab') return;
      const dialog = document.querySelector('.mp-modal');
      const focusable = Array.from(dialog?.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href]') || []);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    };
  }, [showCreateSession, showJoinSession]);

  // Copy Sending Stone to clipboard
  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      alert('Sending Stone copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy Sending Stone:', error);
      alert('Failed to copy link. Please copy manually: ' + inviteLink);
    }
  };

  // Parse Teleportation Circle from URL when component loads
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionIdFromUrl = window.location.pathname.split('/battle/')[1];
    const passwordFromUrl = urlParams.get('password');

    if (sessionIdFromUrl && !currentSessionId) {
      setJoinSessionId(sessionIdFromUrl);
      setJoinPassword(passwordFromUrl || '');
      setShowJoinSession(true);
    }
  }, [currentSessionId]);

  // Load available sessions on mount and when connection established
  useEffect(() => {
    fetchAvailableSessions();
  }, [isConnected]);

  // date_of_change: 2026-04-19 - Save alias to localStorage and keep local state in sync
  const saveAlias = useCallback((value) => {
    const trimmed = value.trim();
    setBattleAlias(trimmed);
    try { localStorage.setItem('vcs_battle_alias', trimmed); } catch (_) {}
    setEditingAlias(false);
  }, []);

  // date_of_change: 2026-04-19 - Show role label first, then the best available name
  const getParticipantDisplayName = (participant) => {
    const isGM = participant.role === 'gm' || participant.isGameMaster;
    const roleLabel = isGM ? 'Game Master' : 'Player';
    // Prefer alias (set by user), then backend userName, then anonymous placeholder
    const name = participant.alias || participant.userName || participant.displayName || null;
    return name ? `${roleLabel} — ${name}` : roleLabel;
  };

  // Show connection status
  if (!isConnected) {
    return (
      <div className="multiplayer-session-manager">
        <div className="connection-status">
          <div className="status-indicator offline"></div>
          <span>Attuning to the multiplayer relay...</span>
        </div>
      </div>
    );
  }

  const visibleSessions = [...availableSessions].sort((left, right) => {
    const inviteDelta = Number(Boolean(right?.isInvited)) - Number(Boolean(left?.isInvited));
    if (inviteDelta !== 0) {
      return inviteDelta;
    }

    const rightUpdatedAt = new Date(right?.updatedAt || right?.createdAt || 0).getTime();
    const leftUpdatedAt = new Date(left?.updatedAt || left?.createdAt || 0).getTime();
    return rightUpdatedAt - leftUpdatedAt;
  });

  // Show current session info above the dashboard controls without hiding room creation/listing.
  // date_of_change: 2025-09-25 - Restored in-session UI and wired Leave to REST+WS
  // date_of_change: 2026-07-03 - Keep the room browser visible while a live room is active.
  const currentSessionPanel = currentSessionId ? (
    <div className="current-session">
          <div className="session-header">
            <div className="status-indicator online"></div>
            <div className="session-info">
              <h3>Battle Room Active</h3>
              <p className="participants-count">
                {sessionParticipants.length} participant{sessionParticipants.length !== 1 ? 's' : ''} connected
              </p>
            </div>
          </div>

          {/* Battle Alias editor */}
          <div className="alias-section">
            <h4>Your Name in Battle</h4>
            {editingAlias ? (
              <div className="alias-edit-row">
                <input
                  type="text"
                  className="alias-input"
                  value={aliasInput}
                  onChange={(e) => setAliasInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveAlias(aliasInput); if (e.key === 'Escape') setEditingAlias(false); }}
                  placeholder="Enter your adventurer name"
                  maxLength={32}
                  autoFocus
                />
                <button className="alias-save-btn" onClick={() => saveAlias(aliasInput)}>Save</button>
                <button className="alias-cancel-btn" onClick={() => setEditingAlias(false)}>Cancel</button>
              </div>
            ) : (
              <div className="alias-display-row">
                <span className="alias-current">{battleAlias || <em>Not set — shown as Player</em>}</span>
                <button className="alias-edit-btn" onClick={() => { setAliasInput(battleAlias); setEditingAlias(true); }}>Edit</button>
              </div>
            )}
          </div>

          {/* Participants List */}
          {sessionParticipants.length > 0 && (
            <div className="participants-list">
              <h4>Gathered Party</h4>
              <ul>
                {sessionParticipants.map((participant) => (
                  <li key={participant.userId}>
                    <span className="participant-name">
                      {getParticipantDisplayName(participant)}
                    </span>
                    {participant.userId === user?.id && <span className="you-indicator">(You)</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Sending Stone */}
          {inviteLink && (
            <div className="invite-section">
              <h4>Invite the Party</h4>
              <div className="invite-link-container">
                <input
                  type="text"
                  value={inviteLink}
                  readOnly
                  className="invite-link-input"
                  onClick={(e) => e.target.select()}
                />
                <button onClick={copyInviteLink} className="copy-button">Copy</button>
              </div>
              <p className="invite-tip">
                Share this link with players to let them join your session
              </p>
            </div>
          )}

          <div className="session-actions">
            <button
              onClick={() => {
                // Navigate to battle room with current session
                if (onSessionJoined) {
                  onSessionJoined(currentSessionId);
                } else {
                  // Fallback: direct navigation
                  window.location.href = `/battle/${currentSessionId}`;
                }
              }}
              className="open-session-button"
            >
              Enter Battle Room
            </button>
            <button onClick={handleLeaveSession} className="leave-button">
              Leave Session
            </button>
          </div>
      </div>
  ) : null;

  return (
    <div className="multiplayer-session-manager">
      {currentSessionPanel}

      <div className="session-options">
        <div className="action-buttons">
          <button
            onClick={() => setShowCreateSession(true)}
            className="create-session-button"
            disabled={loading}
          >
            Create Battle Room
          </button>
          <button
            onClick={() => setShowJoinSession(true)}
            className="join-session-button"
            disabled={loading}
          >
            Join by Runes
          </button>
        </div>
      </div>

      {/* Your Sessions List */}
      <div className="available-sessions">
        <h3>Open Battle Rooms</h3>
        {sessionActionMessage && (
          <div
            className={`session-action-feedback session-action-feedback--${sessionActionMessage.tone}`}
            role={sessionActionMessage.tone === 'error' ? 'alert' : 'status'}
            aria-live={sessionActionMessage.tone === 'error' ? 'assertive' : 'polite'}
          >
            {sessionActionMessage.text}
          </div>
        )}
        {availableSessions.length === 0 ? (
          <div className="no-sessions">No open battle rooms yet. Create one or ask for an invite.</div>
        ) : (
          <div className="session-cards">
            {visibleSessions.map((s) => (
              <div key={s._id} className={`session-card${s.isInvited ? ' session-card--invited' : ''}`}>
                <div className="session-card-header">
                  <div>
                    <div className="session-title">{s.name}</div>
                    <div className="session-card-badges">
                      {s.isOwner && <span className="session-badge session-badge--owner">Your Room</span>}
                      {!s.isOwner && s.isParticipant && <span className="session-badge session-badge--party">Joined</span>}
                      {s.isInvited && <span className="session-badge session-badge--invite">Invited</span>}
                    </div>
                  </div>
                  <div className="session-meta">
                    {s.isInvited
                      ? 'An invite is waiting for you.'
                      : new Date(s.updatedAt || s.createdAt).toLocaleString()}
                  </div>
                </div>
                <div className="session-card-actions">
                  <button
                    onClick={async () => {
                      try {
                        const { apiFetch } = await import('../../utils/apiFetch.js');
                        await apiFetch(`/api/combat/${s._id}/join`, { method: 'POST' });
                        await joinSession(s._id);
                        if (onSessionJoined) onSessionJoined(s._id);
                      } catch (e) {
                        alert('Join failed.');
                      }
                    }}
                    className="join-button"
                  >
                    {s.isInvited ? 'Join Invite' : 'Join'}
                  </button>
                  <button
                    onClick={() => {
                      const link = generateInviteLink(s._id);
                      navigator.clipboard.writeText(link).then(() => {
                        alert('Sending Stone copied!');
                      }).catch(() => {
                        prompt('Copy this link:', link);
                      });
                    }}
                    className="copy-invite-button"
                  >
                    Copy Invite
                  </button>
                  <a className="open-button" href={`/battle/${s._id}`}>Open</a>
                  <button
                    onClick={() => performSessionLeave(s)}
                    className="leave-button"
                  >
                    Leave
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Session Modal */}
      {showCreateSession && (
        <div className="mp-modal-overlay">
          <div className="mp-modal" role="dialog" aria-modal="true" aria-labelledby="create-battle-room-title">
            <div className="mp-modal__header">
              <h3 id="create-battle-room-title">Open a Battle Room</h3>
              <button
                onClick={() => setShowCreateSession(false)}
                className="mp-modal__close"
                aria-label="Close Battle Room creation"
              >
                ×
              </button>
            </div>
            <div className="mp-modal__body">
              <div className="mp-modal__field">
                <label htmlFor="session-name">Session Name *</label>
                <input
                  type="text"
                  id="session-name"
                  value={newSessionName}
                  onChange={(e) => setNewSessionName(e.target.value)}
                  placeholder="Siege of Blackstone Keep"
                  maxLength={50}
                />
              </div>
              <div className="mp-modal__field">
                <label htmlFor="session-description">Description (Optional)</label>
                <textarea
                  id="session-description"
                  value={newSessionDescription}
                  onChange={(e) => setNewSessionDescription(e.target.value)}
                  placeholder="What is the party walking into?"
                  maxLength={200}
                  rows={3}
                />
              </div>
              <div className="mp-modal__field">
                <label htmlFor="session-ruleset">Ruleset *</label>
                <select
                  id="session-ruleset"
                  value={selectedRulesetKey}
                  onChange={(e) => setSelectedRulesetKey(e.target.value)}
                  disabled={loading}
                >
                  {rulesetOptions.length === 0 ? <option value="">Loading rulesets…</option> : null}
                  {rulesetOptions.map((option) => (
                    <option key={option.key} value={option.key}>{option.label}</option>
                  ))}
                </select>
                <p className="mp-modal__help" aria-live="polite">
                  {rulesetLoadMessage || 'This contract governs combat and rest resolution for the room.'}
                </p>
              </div>
              <div className="mp-modal__field">
                <label htmlFor="session-password">Password (Optional)</label>
                <input
                  type="password"
                  id="session-password"
                  value={sessionPassword}
                  onChange={(e) => setSessionPassword(e.target.value)}
                  placeholder="Leave empty for a public room"
                  maxLength={20}
                />
                <p className="mp-modal__help">
                  Add a password if only invited players should enter.
                </p>
              </div>
            </div>
            <div className="mp-modal__footer">
              <button
                onClick={() => setShowCreateSession(false)}
                className="cancel-button"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSession}
                className="create-button"
                disabled={loading || !newSessionName.trim() || rulesetOptions.length === 0 || !selectedRulesetKey}
              >
                {loading ? 'Opening...' : 'Open Battle Room'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Join Session Modal */}
      {showJoinSession && (
        <div className="mp-modal-overlay">
          <div className="mp-modal" role="dialog" aria-modal="true" aria-labelledby="join-battle-room-title">
            <div className="mp-modal__header">
              <h3 id="join-battle-room-title">Join a Battle Room</h3>
              <button
                onClick={() => setShowJoinSession(false)}
                className="mp-modal__close"
                aria-label="Close Battle Room join"
              >
                ×
              </button>
            </div>
            <div className="mp-modal__body">
              <div className="mp-modal__field">
                <label htmlFor="join-session-id">Teleportation Circle or Sending Stone *</label>
                <input
                  type="text"
                  id="join-session-id"
                  value={joinSessionId}
                  onChange={(e) => setJoinSessionId(e.target.value)}
                  placeholder="Paste runes or full Sending Stone"
                />
              </div>
              <div className="mp-modal__field">
                <label htmlFor="join-password">Password (if required)</label>
                <input
                  type="password"
                  id="join-password"
                  value={joinPassword}
                  onChange={(e) => setJoinPassword(e.target.value)}
                  placeholder="Enter ward phrase if needed"
                />
              </div>
            </div>
            <div className="mp-modal__footer">
              <button
                onClick={() => setShowJoinSession(false)}
                className="cancel-button"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleJoinSession}
                className="join-button"
                disabled={loading || !joinSessionId.trim()}
              >
                {loading ? 'Joining...' : 'Join Battle Room'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiplayerSessionManager;
